import { AuditDataModel, IMissingTitle } from "../types";
import { criticalListLeft, warningListLeft } from "../constants";

function fixRelativeUrls(doc: Document, baseUrl: string) {
  const base = new URL(baseUrl);
  doc.querySelectorAll("img").forEach((img) => {
    if (img.src) {
      try {
        img.src = new URL(img.src, base).href;
      } catch (e) { }
    }
  });
  doc.querySelectorAll("a").forEach((a) => {
    if (a.href) {
      try {
        a.href = new URL(a.href, base).href;
      } catch (e) { }
    }
  });
}

async function fetchPageHTML(url: string): Promise<Document | null> {
  try {
    const corsProxy = "https://api.allorigins.win/raw?url=";
    const response = await fetch(corsProxy + encodeURIComponent(url), {
      headers: { "User-Agent": "Mozilla/5.0" },
    });
    if (!response.ok || response.status !== 200) {
      console.warn(`Failed to fetch ${url}: Status ${response.status}`);
      return null;
    }
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    fixRelativeUrls(doc, url);
    return doc;
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    return null;
  }
}

function extractLinks(doc: Document, baseUrl: string): string[] {
  const links: Set<string> = new Set();
  const baseHostname = new URL(baseUrl).hostname;

  // Extract from <a> tags
  doc.querySelectorAll("a[href]").forEach((a) => {
    let href = a.getAttribute("href");
    if (href) {
      try {
        const absoluteUrl = new URL(href, baseUrl).href;
        const urlObj = new URL(absoluteUrl);
        if (urlObj.hostname === baseHostname && !urlObj.hash) {
          links.add(absoluteUrl);
        }
      } catch (e) {
        console.warn(`Invalid URL skipped: ${href}`);
      }
    }
  });

  return Array.from(links);
}

// Crawl the website starting from the given URL
export async function crawlWebsite(startUrl: string, maxPages: number = 100): Promise<AuditDataModel> {
  const visited = new Set<string>();
  const toVisit: string[] = [startUrl];
  const result: AuditDataModel = { missingTitles: [], url: startUrl, metaTags: { title: "", description: "", canonical: "", robots: "", issues: [] }, headings: { h1: [], h2: [], h3: [], issues: [] }, images: [], technical: { internalLinks: [], brokenLinks: [], structuredData: null, issues: [] }, performance: { loadTime: 0, mobileOptimized: false, coreWebVitals: { lcp: 0, fid: 0, cls: 0 }, issues: [] }, security: { https: false, mixedContent: false, sslValid: false, issues: [] }, score: 0 };

  while (toVisit.length > 0 && visited.size < maxPages) {
    const url = toVisit.shift();
    if (!url || visited.has(url)) continue;

    visited.add(url);
    const doc = await fetchPageHTML(url); // Your fetch logic
    if (!doc) continue;

    const analysis = await analyzeMissingTitle(doc, url); // Your analysis logic
    result.missingTitles.push(analysis);

    const links = extractLinks(doc, url); // Your link extraction logic
    links.forEach((link) => {
      if (!visited.has(link) && !toVisit.includes(link)) toVisit.push(link);
    });
  }

  return result;
}

// Analyze a single page for SEO issues
async function analyzeMissingTitle(doc: Document, url: string): Promise<IMissingTitle> {
  const titleElements = doc.getElementsByTagName("title");
  const metaDescriptions = doc.querySelectorAll('meta[name="description"]');
  const h1Elements = doc.getElementsByTagName("h1");
  const images = doc.getElementsByTagName("img");
  const scripts = doc.getElementsByTagName("script");
  const links = doc.getElementsByTagName("a");
  const bodyText = doc.body.innerText.trim();
  const title = doc.title || "";
  const description = metaDescriptions.length
    ? metaDescriptions[0].getAttribute("content") || ""
    : "";
  const canonical =
    doc.querySelector('link[rel="canonical"]')?.getAttribute("href") || "";
  const noindex =
    doc
      .querySelector('meta[name="robots"]')
      ?.getAttribute("content")
      ?.includes("noindex") || false;
  const robotsTxtExists = await checkRobotsTxt();
  const is404 = check404Page(doc);
  const isCompressed = await checkTextCompression(url);
  const seoFriendlyURL = isSEOFriendlyURL(url);
  const hasStructuredData = checkStructuredData(doc);
  const hasMetaViewport = doc.querySelector('meta[name="viewport"]') !== null;
  const langAttribute = doc.documentElement.lang || "";
  const faviconExists = doc.querySelector('link[rel="icon"]') !== null;
  const loadTime = getLoadTime();
  const pageSize = getPageSize();

  const issues: string[] = [];

  if (!title) issues.push("Missing Title");
  if (title.length < 30) issues.push("Title Too Short");
  if (title.length > 60) issues.push("Title Too Long");
  if (titleElements.length > 1) issues.push("Multiple Title");

  if (!description) issues.push("Missing Description");
  if (description.length < 50) issues.push("Description Too Short");
  if (description.length > 160) issues.push("Description Too Long");
  if (metaDescriptions.length > 1) issues.push("Multiple Description");
  const descriptionSet = new Set<string>();
  for (const meta of metaDescriptions) {
    const descText = meta.getAttribute("content")?.trim();
    if (descText) {
      if (descriptionSet.has(descText)) issues.push("Duplicate Description");
      descriptionSet.add(descText);
    }
  }

  if (h1Elements.length === 0) issues.push("Missing H1 Tag");
  if (h1Elements.length > 1) issues.push("Multiple H1 Tags");

  for (const img of images) {
    if (!img.alt || img.alt.trim() === "") {
      issues.push("Missing Image Alt Text");
      break;
    }
    if (!/\.(webp|png|jpe?g)$/i.test(img.src)) {
      issues.push("Image Format");
      break;
    }
  }

  for (const script of scripts) {
    if (!script.hasAttribute("defer") && !script.hasAttribute("async")) {
      issues.push("JavaScript defer");
      break;
    }
  }
  if (doc.getElementsByTagName("*").length > 1500) {
    issues.push("DOM Size");
  }

  if (!doc.querySelector('meta[charset="UTF-8"]')) {
    issues.push("Character Set");
  }

  if (!checkSitemap()) {
    issues.push("Sitemap");
  }

  const wordCount = bodyText.split(/\s+/).length;
  if (wordCount < 300) {
    issues.push("Content Length");
  }

  const htmlSize = new Blob([doc.documentElement.outerHTML]).size;
  const textToHtmlRatio = (bodyText.length / htmlSize) * 100;
  if (textToHtmlRatio < 10) {
    issues.push("Text to HTML ratio");
  }

  const inlineStyles = doc.querySelectorAll("[style]");
  if (inlineStyles.length > 50) {
    issues.push("Inline CSS");
  }

  let brokenLinks = 0;
  for (const link of links) {
    if (!link.href || link.href.includes("javascript:void(0)")) {
      brokenLinks++;
    }
  }
  if (links.length > 100 || brokenLinks > 0) {
    issues.push("In-page Links");
  }

  const deprecatedTags = ["font", "center", "marquee", "bgsound"];
  for (const tag of deprecatedTags) {
    if (doc.getElementsByTagName(tag).length > 0) {
      issues.push("Deprecated HTML");
    }
  }

  if (!isCompressed) issues.push("Text compression");
  if (seoFriendlyURL) issues.push("SEO friendly URL");
  if (robotsTxtExists) issues.push("Robots.txt");
  if (noindex) issues.push("noindex");
  if (is404) issues.push("404 page");
  const titleSet = new Set<string>();
  for (const titleElement of titleElements) {
    const text = titleElement.textContent?.trim();
    if (text) {
      if (titleSet.has(text)) issues.push("Duplicate Title");
      titleSet.add(text);
    }
  }
  if (!hasStructuredData) issues.push("Structured Data");
  if (!hasMetaViewport) issues.push("Meta viewport");
  if (!langAttribute) issues.push("Language");
  if (!faviconExists) issues.push("Favicon");
  if (loadTime > 3000) issues.push("Load Time");
  const textViewportCheck = isTextVisibleInViewport();
  if (!textViewportCheck) issues.push("Text viewport");
  if (pageSize > 2 * 1024 * 1024) issues.push("Page Size");

  const importantKeywords = ["SEO", "audit", "website"];
  if (h1Elements.length > 0) {
    issues.push("Heading Tag");
  }
  const checkTitle = await checkTitlePassed(url);
  if (checkTitle) issues.push("Title");
  const checkMetaDescription = await checkMetaDescriptionPassed(url);
  if (checkMetaDescription) issues.push("Meta Description");

  for (const keyword of importantKeywords) {
    const density = keywordDensity(bodyText, keyword);
    if (density < 2 || density > 5) {
      issues.push("Content Keywords");
    }
  }
  for (const img of images) {
    const fileName = img.src.split("/").pop()?.toLowerCase() || "";
    if (!hasImportantKeyword(fileName, importantKeywords)) {
      issues.push("Image Keywords");
    }
  }
  const safeBrowsing = await checkGoogleSafeBrowsing(url);
  if (safeBrowsing) issues.push("Google Safe Browsing");
  const isIndexed = await checkIndexedStatus(url);
  if (isIndexed) issues.push("indexed");
  if (!isIndexed) issues.push("noindex");
  const robotsTxtContent = await fetchRobotsTxt();
  if (robotsTxtContent.includes("Disallow: /")) {
    issues.push("Robots.txt");
  }

  const criticalIssues = new Set(criticalListLeft.map((item) => item.name));
  const warningIssues = new Set(warningListLeft.map((item) => item.name));
  const numCritical = issues.filter((issue) => criticalIssues.has(issue)).length;
  const numWarning = issues.filter((issue) => warningIssues.has(issue)).length;
  const highIssues = 130 - numCritical * 10;
  const mediumIssues = 50 - numWarning * 5;
  const onePageScore = ((highIssues + mediumIssues) / (130 + 50)) * 100;
  const score = Math.max(0, Math.min(100, Math.round(onePageScore)));

  return { title, description, issues, canonical, score };
}

// Helper functions
async function fetchRobotsTxt(): Promise<string> {
  try {
    const response = await fetch("/robots.txt");
    return response.ok ? await response.text() : "";
  } catch {
    return "";
  }
}

async function checkIndexedStatus(url: string): Promise<boolean> {
  try {
    const response = await fetch(
      `https://development.mydevelopmentserver.io/api/v1/proxy-data`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: `https://serpapi.com/search.json?engine=google&q=site:${url}&api_key=ea15558d39e42c44674e38a2da13860ad9e6e02e9c50ac824a7999667d69fe53`,
          responseType: "json",
        }),
      }
    );
    const html = await response.text();
    const isNoindexMeta =
      /<meta\s+name=["']robots["']\s+content=["'][^"']*noindex[^"']*["']\s*>/i.test(
        html
      );
    const xRobotsTag = response.headers.get("X-Robots-Tag");
    const isNoindexHeader =
      xRobotsTag && xRobotsTag.toLowerCase().includes("noindex");
    return !(isNoindexMeta || isNoindexHeader);
  } catch (error) {
    console.log(error);
    return false;
  }
}

async function checkGoogleSafeBrowsing(url: string): Promise<boolean> {
  try {
    const response = await fetch(
      `https://development.mydevelopmentserver.io/api/v1/proxy-data`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: `https://serpapi.com/search.json?engine=google&q=site:${url}&api_key=ea15558d39e42c44674e38a2da13860ad9e6e02e9c50ac824a7999667d69fe53`,
          responseType: "json",
        }),
      }
    );
    const { data } = await response.json();
    const warningPhrases = [
      "This site may harm your computer",
      "this site may be hacked",
      "deceptive site ahead",
      "dangerous site",
    ];
    return !data.organic_results.some((result: any) =>
      warningPhrases.some((phrase) =>
        result.snippet.toLowerCase().includes(phrase)
      )
    );
  } catch (error) {
    console.error("Error checking safe browsing status:", error);
    return false;
  }
}

const checkTitlePassed = async (url: string) => {
  try {
    const response = await fetch(
      `https://development.mydevelopmentserver.io/api/v1/proxy-data`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: `https://serpapi.com/search.json?engine=google&q=site:${url}&api_key=ea15558d39e42c44674e38a2da13860ad9e6e02e9c50ac824a7999667d69fe53`,
          responseType: "json",
        }),
      }
    );
    const { data } = await response.json();
    if (data) {
      const isTitleAvailable = data.organic_results.every(
        (result: any) => !!result.title && result.title.trim() !== ""
      );
      return isTitleAvailable;
    }
    return false;
  } catch (error) {
    console.log(error);
    return false;
  }
};

const checkMetaDescriptionPassed = async (url: string) => {
  try {
    const response = await fetch(
      `https://development.mydevelopmentserver.io/api/v1/proxy-data`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: `https://serpapi.com/search.json?engine=google&q=site:${url}&api_key=ea15558d39e42c44674e38a2da13860ad9e6e02e9c50ac824a7999667d69fe53`,
          responseType: "json",
        }),
      }
    );
    const { data } = await response.json();
    if (data) {
      const isSnippetAvailable = data.organic_results.every(
        (result: any) => !!result.snippet && result.snippet.trim() !== ""
      );
      return isSnippetAvailable;
    }
    return false;
  } catch (error) {
    console.log(error);
    return false;
  }
};

function keywordDensity(content: string, keyword: string): number {
  const words = content.split(/\s+/).length;
  const keywordCount = (
    content.match(new RegExp(`\\b${keyword}\\b`, "gi")) || []
  ).length;
  return (keywordCount / words) * 100;
}

function hasImportantKeyword(text: string, keywords: string[]): boolean {
  return keywords.some((keyword) =>
    text.toLowerCase().includes(keyword.toLowerCase())
  );
}

function isTextVisibleInViewport(): boolean {
  const bodyText = document.body.innerText.trim();
  const visibleText: string[] = [];
  document
    .querySelectorAll("p, h1, h2, h3, h4, h5, h6, li, span, div")
    .forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
        const text = (element as HTMLElement).innerText.trim();
        if (text) visibleText.push(text);
      }
    });
  const visibleTextLength = visibleText.join(" ").length;
  const totalTextLength = bodyText.length;
  return visibleTextLength / totalTextLength > 0.2;
}

function checkSitemap() {
  return fetch("/sitemap.xml", { method: "HEAD" })
    .then((response) => response.ok)
    .catch(() => false);
}

function getLoadTime() {
  if (performance.timing) {
    return performance.timing.loadEventEnd - performance.timing.navigationStart;
  }
  return 0;
}

function getPageSize() {
  if (performance.getEntriesByType) {
    return performance
      .getEntriesByType("resource")
      .reduce(
        (total, resource) =>
          total + (resource as PerformanceResourceTiming).transferSize,
        0
      );
  }
  return 0;
}

function checkStructuredData(doc: Document) {
  return doc.querySelector('script[type="application/ld+json"]') !== null;
}

function checkRobotsTxt() {
  return fetch("/robots.txt")
    .then((response) => response.ok)
    .catch(() => false);
}

function checkTextCompression(url: string) {
  return fetch(url, { method: "HEAD", mode: "no-cors" })
    .then((response) => {
      const encoding = response.headers.get("content-encoding");
      return encoding && (encoding.includes("gzip") || encoding.includes("br"));
    })
    .catch(() => false);
}

function isSEOFriendlyURL(url: string) {
  try {
    url = url.replace(/^(https?:\/\/)?(www\.)?/i, "").replace(/\/$/, "");
    const isValid = /^[a-z0-9-/.]+$/.test(url);
    const hasUnderscore = url.includes("_");
    const isTooLong = url.length >= 100;
    return isValid && !hasUnderscore && !isTooLong;
  } catch (error) {
    console.error("Error checking SEO-friendly URL:", error);
    return false;
  }
}

function check404Page(doc: Document) {
  const bodyText = doc.body.textContent || "";
  return /404|page not found|not available/i.test(bodyText);
}